import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

type DirtyItem = { id: string; [key: string]: any }

interface DirtyWorkContextValue {
  addData: (type: string, items: DirtyItem[]) => void
  patchData: (type: string, defaultItem: DirtyItem, updater: (item: DirtyItem) => Partial<DirtyItem> | undefined) => void
  mapData: (type: string, items: DirtyItem[]) => DirtyItem[]
}

const SharedDirtyWorkContext = createContext<DirtyWorkContextValue>({
  addData: (_type: string, _items: DirtyItem[]) => {},
  patchData: (_type: string, _defaultItem: DirtyItem, _updater: (item: DirtyItem) => Partial<DirtyItem> | undefined) => {},
  mapData: (_type: string, items: DirtyItem[]) => items,
})

interface DirtyWorkDataProviderProps {
  children: React.ReactNode
}

export const DirtyWorkDataProvider = ({ children }: DirtyWorkDataProviderProps) => {
  const [data, setData] = useState<{ [type: string]: { [id: string]: DirtyItem } }>(() => ({}))

  const addData = useCallback((type: string, items: DirtyItem[]) => {
    setData((oldValue) => {
      const oldRecord = oldValue[type] ?? {}
      const newRecord = { ...oldRecord }
      for (const item of items) {
        newRecord[item.id] = item
      }
      return { ...oldValue, [type]: newRecord }
    })
  }, [])

  const patchData = useCallback(
    (type: string, defaultItem: DirtyItem, updater: (item: DirtyItem) => Partial<DirtyItem> | undefined) => {
      setData((oldValue) => {
        const oldRecord = oldValue[type] ?? {}
        const id = defaultItem.id
        const oldItem = oldRecord[id] ?? defaultItem
        const patch = updater(oldItem)
        if (patch == null) return oldValue
        return { ...oldValue, [type]: { ...oldRecord, [id]: { ...oldItem, ...patch, id } } }
      })
    },
    []
  )

  const mapData = useCallback(
    (type: string, items: DirtyItem[]) => {
      if (data[type] == null) {
        return items
      }

      const record = data[type]

      return items.map((i) => {
        if (record[i.id]) {
          return record[i.id]
        }
        return i
      })
    },
    [data]
  )

  const value = useMemo(
    () => ({
      addData,
      patchData,
      mapData,
    }),
    [addData, patchData, mapData]
  )

  return <SharedDirtyWorkContext.Provider value={value}>{children}</SharedDirtyWorkContext.Provider>
}

export const useSharedCache = () => {
  const { addData, patchData, mapData } = useContext(SharedDirtyWorkContext)

  const addDirtyData = addData
  const patchDirtyData = patchData

  const useMappedData = (type: string, items: DirtyItem[]) => {
    return useMemo(() => {
      return mapData(type, items)
      // FUCK eslint, ctx really changes and cause update!
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapData, items, type])
  }

  return { addDirtyData, patchDirtyData, useMappedData }
}
